import React, { PureComponent } from 'react';

import eyeClosed from '../../assets/img/icons/eye-closed.svg';
import eyeOpened from '../../assets/img/icons/eye-opened.svg';
import Tooltip from '../tooltip';
import Calendar from './calendar';
import { CALENDAR_MONTHS_FOR_DATE, convertDate, getDateToShow } from './calendar/calendarHelpers';
import AutoComplete from './email/autocomplete';
import { CalendarIcon,
    DescriptionBlock,
    Eye,
    InputContainer,
    InputError,
    PrettyText,
    QuestionIcon,
    StyledInput } from './styles';

export const VALIDATION_REGEXP = {
    name: /[0-9!"№;%:?*()_+=~[\]`@#$^&{},.<>/|\\']/,
    cyrillicName: /^([а-яёА-ЯЁ]+[-']?[а-яёА-ЯЁ]*\s??){0,5}$/,
    password: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[^*а-яА-ЯёЁ"@№;$%:^<>|=&+?~`(){},]{8,50}$/,
    agreement: /\b([A-z0-9]){5,16}\b/,
    email: /^[a-z0-9._+-]+@[a-z0-9.-]+\.[a-z]{2,64}/i,
    phone: /^((\+7|7|8)+([0-9]){10})$/,
    login: /[a-z0-9]+/i,
    amount: /^(0|[1-9]\d*)(\.\d{0,2})?$/,
    integer: /^[+-]?\d+$/i,
    date: /(19|20)\d\d[- /.](0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])/,
    passportSeries: /([0-9]){4}/i,
    passportNumber: /([0-9]){6}/i,
    passportFull: /\d{4}\d{6}/i,
    card: /([0-9]){4}/i,
    mac: /^([0-9A-Fa-f]{12})$/,
};

const MASK = {
    phone: '+{7}(000)000-00-00',
    date: '00.00.0000',
    amount: VALIDATION_REGEXP.amount,
    passportSeries: '00 00',
    passportNumber: '000000',
    passportFull: '0000 000000',
    card: '0000',
    mac: '**:**:**:**:**:**',
};

class Input extends PureComponent {
    static defaultProps = {
        onChange: () => {},
        onBlur: () => {},
        onKeyUp: () => {},
        lazyMask: true,
    };

    // Added as a component property
    static defaultComponentState = {
        hasError: false,
        showErrorText: false,
        touched: false,
        showText: true,
        prettyText: false,
        tempValue: null,
        inputVal: null,
        showHelper: false,
        isFocused: false,
        noBlurValidation: false,
    };

    constructor(props) {
        super(props);
        // Set the default state immediately
        this.state = {
            ...Input.defaultComponentState,
        };
        this.inputRef = React.createRef();
    }

    resetStateWithUpdates(stateUpdates = {}) {
        // Rest operators ensure a new object with merged properties and values.
        // Requires the "transform-object-rest-spread" Babel plugin
        this.setState({ ...Input.defaultComponentState, ...stateUpdates }, () => {
            if (this.mask) {
                this.mask.value = '';
                this.mask.updateValue();
            }
        });
    }

    componentDidMount() {
        const { value, type, defaultValue, lazyMask } = this.props;
        this.validate(value);
        this.showText(type !== 'hidden' && !['password', 'fakePassword'].includes(type));
        const input = this.inputRef.current;
        const mask = MASK[type];
        if (input && mask) {
            import('imask').then(({ default: IMask }) => {
                this.mask = new IMask(input, {
                    mask: mask,
                    lazy: lazyMask,
                    prepare: (added, masked) => {
                        if (this.props.type === 'phone' && added === '8' && masked.value === '') {
                            return '';
                        }
                        return added;
                    },
                });
                this.mask.alignCursor();
            });

            if (type === 'date') {
                if (window) {
                    window.addEventListener('click', this.hideHelper);
                }
                this.setTempValue(defaultValue);
            }
        }
    }

    componentWillUnmount() {
        const { type } = this.props;
        if (this.mask && this.mask.destroy) {
            this.mask.destroy();
        }
        if (type === 'date') {
            window.removeEventListener('click', this.hideHelper);
        }
    }

    // если есть minDate или maxDate, после выбора даты возможен ввод, нужно проверить значение
    checkDate = (value) => {
        const { minDate, maxDate } = this.props;
        let isValid = true,
            isValidMin,
            isValidMax;
        const valueDate = new Date(value);

        if (minDate) {
            const minD = new Date(minDate);
            isValidMin = valueDate > minD;
            isValid = isValidMin;
        }
        if (maxDate) {
            const maxD = new Date(maxDate);
            isValidMax = valueDate < maxD;
            isValid = isValidMin ? isValidMin && isValidMax : isValidMax;
        }
        return isValid;
    }

    validate = (value) => {
        // TODO: Требуется рефакторинг этой пачки if/else, а лучше всего компонента
        // eslint-disable-next-line sonarjs/cognitive-complexity
        return new Promise((resolve) => {
            const { type, min, max, required, validationRegexp } = this.props;
            const { hasError } = this.state;
            const validator = validationRegexp ? new RegExp(validationRegexp) : VALIDATION_REGEXP[type];
            if (validator !== undefined) {
                let invalid = type === 'name'
                    ?   validator.test(value)
                    :   !validator.test(value);
                if ((min && Number(value) < min) || (max && Number(value) > max)) {
                    invalid = true;
                }

                if (!required && value === '') {
                    invalid = false;
                }
                if (type === 'name' && required && value === '') {
                    invalid = true;
                }

                if (type === 'date') {
                    invalid = invalid || !this.checkDate(value);
                }

                if (hasError !== invalid) {
                    this.setHasError(!hasError).then((res) => {
                        resolve(res);
                    });
                } else {
                    resolve(!hasError);
                }
            } else {
                resolve(true);
            }
        });

    }

    check = (value) => {
        return new Promise((resolve) => {
            if (value !== undefined) {
                this.setTouched(true);
                this.validate(value).then((res) => {
                    resolve(res);
                });
            } else {
                resolve(true);
            }
        });
    }

    setTouched = (value = true) => {
        this.setState({
            touched: value,
        });
    }

    setHasError = (value = true) => {
        return new Promise((resolve) => {
            this.setState({
                hasError: value,
            }, () => {
                resolve(!value);
            });
        });

    }

    setShowError = (value = false) => {
        this.setState({
            showErrorText: value,
        });
    }

    onKeyPress = (event, regexpToReplace, replaceTo, convertFunc) => {
        if (event.which === 13) {
            this.setState({ tempValue: null });
            const value = this.getValue(event, regexpToReplace, replaceTo, convertFunc);
            this.check(value).then((isValid) => {
                if (isValid) {
                    this.setPrettyText(value);
                    this.props.onChange(value);
                }
            });
        }
    }

    showText = (value) => {
        this.setState({
            showText: value,
        });
    }

    blur = (event, regexpToReplace, replaceTo, convertFunc) => {
        this.toggleFocus();

        if (this.state.noBlurValidation) {
            return;
        }

        const value = this.getValue(
            event,
            regexpToReplace,
            replaceTo,
            convertFunc,
        );

        this.check(value)
            .then(isValid => {
                if (isValid) {
                    this.setPrettyText(value);
                    this.setState({ inputVal: value });
                } else {
                    this.setState({ inputVal: null });
                }
                this.setShowError(this.state.hasError);
            })
            .then(() => {
                this.props.onBlur(event, value);
            });
    }

    getValue = (event, regexpToReplace, replaceTo, convertFunc) => {
        const { type } = this.props;
        let value = this.mask && type !== 'date' ? this.mask.unmaskedValue : event.target.value;
        if (regexpToReplace) {
            value = value.replace(regexpToReplace, replaceTo ? replaceTo : '');
        }
        if (!this.mask && type !== 'date') {
            value = value.replace(/(^\s*)/gi, '')
                .replace(/[ ]{2,}/gi, ' ');
        }
        if  (convertFunc) {
            value = convertFunc(value);
        }
        return value;
    }

    change = (event, regexpToReplace, replaceTo, convertFunc) => {
        const { type } = this.props;
        if (this.mask) {
            this.mask.updateValue();
        }
        this.setState({ tempValue: event.target.value });
        const value = this.getValue(event, regexpToReplace, replaceTo, convertFunc);
        this.setShowError();
        this.check(value).then((isValid) => {
            if (isValid || type !== 'date') {
                this.props.onChange(value);
            }
        });
    }

    onPaste = (event) => {
        if (this.mask) {
            const masked = this.mask.masked;
            const text = event.clipboardData.getData('text');
            masked.value = text;
            this.props.onChange(masked.unmaskedValue);
        }
    }

    setPrettyText = (value) => {
        const { type } = this.props;
        if (value && type === 'date') {
            const dateArr = value.split('-');
            let prettyText = null;
            const date = new Date(value);
            if (date) {
                prettyText = `${dateArr[2]} ${CALENDAR_MONTHS_FOR_DATE[dateArr[1] - 1]} ${dateArr[0]}`;
            }
            this.setState({ prettyText });
        }
    }

    hidePretty = () => {
        this.setState({ prettyText: null });
        this.inputRef.current.focus();
    }

    setTempValue = (value) => {
        const date = new Date(value);
        const showDate = getDateToShow(date);
        this.setPrettyText(value);

        this.setState({
            tempValue: showDate,
            inputVal: value,
            showHelper: false,
        }, () => {
            if (this.mask) {
                this.mask.updateValue();
                this.check(value);
                this.props.onChange(value);
            }
        });
    }

    toggleFocus = () => {
        this.setState((prevState) => ({
            isFocused: !prevState.isFocused,
        }));
    }

    showHelper = (e, def) => {
        e.stopPropagation();
        const { showHelper } = this.state;
        const show = def || !showHelper;
        this.setState({ showHelper: show });
    }

    hideHelper = (e) => {
        const { showHelper } = this.state;
        showHelper ? this.showHelper(e, false) : null;
    }

    /**
     * Set email input value after selecting one from the list,
     * and validate it
     * @param {String} value email value, selected in the autocompletion list
     */
    onEmailSelect = value => {
        this.check(value).then(isValid => {
            this.setShowError(this.state.hasError);

            this.pushOnChangeEvent(value);

            this.setState({
                inputVal: isValid ? value : null,
                tempValue: value,
            });
        });
    };

    /**
     * Force onchange event on the input after email autocompletion form is closed
     * @param {String} value
     */
    pushOnChangeEvent(value) {
        if (!value) {
            return;
        }

        const input = this.inputRef.current;
        const valueSetter = Object.getOwnPropertyDescriptor(input, 'value')
            .set;
        const prototype = Object.getPrototypeOf(input);
        const prototypeValueSetter = Object.getOwnPropertyDescriptor(
            prototype,
            'value',
        ).set;

        if (valueSetter && valueSetter !== prototypeValueSetter) {
            prototypeValueSetter.call(input, value);
        } else {
            valueSetter(input, value);
        }

        input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    setContainerRef = el => {
        this.containerRef = el;
    };

    setNoBlurValidation = (val = false) => {
        this.setState(() => ({
            noBlurValidation: val,
        }));
    };

    getError = (notValid) => {
        const { errorMessage, showErrorText, manualError } = this.props;

        if (manualError) {
            return <InputError show>{manualError}</InputError>;
        }

        return errorMessage && showErrorText ? <InputError show={notValid}>{errorMessage}</InputError> : null;
    }

    // TODO: Требуется рефакторинг этой пачки if/else, а лучше всего компонента
    // eslint-disable-next-line sonarjs/cognitive-complexity
    render() {
        // attrs - стандартные атрибуты
        const {
            type,
            value,
            required,
            autoComplete = 'off',
            className,
            minDate,
            maxDate,
            placeholder,
            emailDomains,
            description,
            tooltipText = '',
            minLength = -1,
            manualError,
            ...attrs
        } = this.props;
        const { touched, hasError, showText, prettyText, tempValue, inputVal, showHelper, isFocused } = this.state;
        let icon = null;
        let inputType = type || 'text';
        attrs.minLength = minLength;
        attrs.onChange = event => this.change(event);
        if ((type === 'phone') && MASK[type]) {
            attrs.onKeyUp = event => this.change(event);
        }
        attrs.onBlur = event => this.blur(event);
        switch (type) {
        case 'amount':
            inputType = 'tel';
            attrs.onChange = event => this.change(event, /,/g, '.');
            attrs.onBlur = event => this.blur(event, /,/g, '.');
            break;
        case 'date':
            inputType = 'text';
            attrs.onChange = event => this.change(event, null, null, convertDate);
            attrs.onBlur = event => this.blur(event, null, null, convertDate);
            attrs.onKeyPress = event => this.onKeyPress(event, null, null, convertDate);
            icon = <CalendarIcon onClick={(e) => this.showHelper(e)} />;
            break;
        case 'phone':
        case 'integer':
            inputType = 'tel';
            attrs.onChange = event => this.change(event, /\D/g);
            break;
        case 'hidden':
        case 'fakePassword':
        case 'password':
            inputType = showText
                ? 'text'
                : type === 'fakePassword' ? 'text' : 'password';
            icon = <Eye
                alt={'Скрыть/Показать'}
                src={showText ? eyeOpened : eyeClosed}
                open={showText}
                onTouchStart={() => this.showText(!showText)}
                onClick={() => this.showText(!showText)}
            />;
            attrs.onChange = event => this.change(event, /\s/ig);
            break;
        case 'agreement':
            inputType = 'text';
            attrs.maxLength = 16;
            break;
        case 'email':
        case 'login':
            attrs.onChange = event => this.change(event, /[а-яё ]/ig);
            break;
        default:
            break;
        }

        const hasMask = this.mask && this.mask.masked.unmaskedValue;
        const emptyValue = !(hasMask || (this.inputRef.current && this.inputRef.current.value) || value);
        const valueLengthGreaterThanMin = required ? !emptyValue && (tempValue || value)?.length >= minLength : true;
        const valid = touched && !hasError && ((!emptyValue && !required) || (!emptyValue && required));
        const notValid = touched && ((required && emptyValue) || hasError || !valueLengthGreaterThanMin) || manualError;
        const pretty = prettyText ? <PrettyText onClick={this.hidePretty}>{prettyText}</PrettyText> : null;
        return (
            <InputContainer className={className} ref={this.setContainerRef}>
                <StyledInput
                    ref={(element) => {
                        this.inputRef.current = element;
                        this.props.inputRef?.(element);
                    }}
                    value={tempValue || value}
                    type={inputType}
                    notValid={notValid}
                    valid={valid}
                    onKeyPress={this.onKeyPress}
                    onPaste={this.onPaste}
                    onFocus={this.toggleFocus}
                    showText={showText}
                    required={required}
                    autoComplete={autoComplete}
                    pretty={pretty}
                    placeholder={isFocused || value ? '' : placeholder}
                    isHasMask={hasMask}
                    isIconExist={icon}
                    data-test='input'
                    open={showText}
                    {...attrs}
                />
                {pretty}
                {
                    tooltipText
                        ? <Tooltip
                            position='bottom'
                            arrow='center'
                            parent={<QuestionIcon onClick={this.showHelper}/>}
                            stateful={true}
                        >
                            {tooltipText}
                        </Tooltip>
                        : icon
                }
                {this.getError(notValid)}
                {description && <DescriptionBlock>{description}</DescriptionBlock>}
                {type === 'date' &&
                    <Calendar
                        showCalendar={showHelper}
                        onChange={this.setTempValue.bind(this)}
                        nowDate={inputVal}
                        minDate={minDate}
                        maxDate={maxDate}
                        data-test='calendar'
                    />
                }
                {
                    type ===  'email' &&
                    <AutoComplete
                        value={value}
                        handleSelect={this.onEmailSelect}
                        setNoBlurValidation={this.setNoBlurValidation}
                        parentRef={this.containerRef}
                        domains={emailDomains}
                        data-test='autocomplete'
                    />
                }
            </InputContainer>
        );
    }
}

export default Input;
